import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { useAsync, Banner, Empty } from './ui';
import { FIELD_META } from '../../lib/readingColumns';
import { sharePmsReportPdf } from '../../lib/sharePmsReport';
import { pmsGroupLabel, groupBy } from '../../lib/pmsGroup';

type Row = Record<string, unknown>;
const LEAD = new Set([
  'Submission ID', 'Server Timestamp', 'Client Captured At', 'Effective Reading At', 'Source',
  'Date', 'Time', 'NFC Tag Code', 'Equipment ID', 'Equipment Name', 'Location', 'Operator ID', 'Operator Name',
  'Observation / Remarks', 'Abnormal Status', 'Abnormal Fields', 'Operator Confirmed Abnormal',
  'Sync Status', 'Reject Reason', 'App Version', 'Approved By', 'Approved By Name', 'Approved At',
]);
const daysAgoISO = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/** Per-visit PMS checklists, grouped by equipment type — review, approve, and generate the report PDF. */
export default function PmsReportsAdmin() {
  const [sp] = useSearchParams();
  const initial = {
    from: sp.get('from') || daysAgoISO(30),
    to: sp.get('to') || daysAgoISO(0),
    equipmentId: sp.get('equipmentId') || '',
    operatorId: sp.get('operatorId') || '',
    approval: sp.get('approval') || '',
  };
  const [filter, setFilter] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const templates = useAsync(() => adminApi.listTemplates(), [], 'templates');
  const equipment = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const { data, error, loading, reload } = useAsync(
    () => adminApi.listReadings(clean(applied), 500),
    [applied],
    `pmsReadings:${JSON.stringify(clean(applied))}`,
  );
  const [busyId, setBusyId] = useState('');
  const [rowMsg, setRowMsg] = useState<Record<string, string>>({});

  const equipById = useMemo(() => {
    const m = new Map<string, {
      templateId: string; category: string; targetIntervalMin: number; pmsGroup: string;
      manufacturer: string; pmsNumber: string; uniqueId: string; yearOfInstallation: string;
      serialNumber: string; capacity: string;
    }>();
    const tplTitleById = new Map(templates.data?.templates.map((t) => [t.templateId, t.title]));
    equipment.data?.rows.forEach((r) => {
      const templateId = String(r['Template ID'] ?? '');
      m.set(String(r['Equipment ID']), {
        templateId, category: String(r['Category'] ?? 'daily'),
        targetIntervalMin: Number(r['Target Interval (min)']) || 21600,
        pmsGroup: pmsGroupLabel(tplTitleById.get(templateId) ?? templateId) || 'Other',
        manufacturer: String(r['Manufacturer'] ?? ''), pmsNumber: String(r['PMS Number'] ?? ''),
        uniqueId: String(r['Unique ID'] ?? ''), yearOfInstallation: String(r['Year of Installation'] ?? ''),
        serialNumber: String(r['Serial Number'] ?? ''), capacity: String(r['Capacity'] ?? ''),
      });
    });
    return m;
  }, [equipment.data, templates.data]);

  const pmsEquipment = useMemo(
    () => (equipment.data?.rows ?? []).filter((r) => String(r['Category'] ?? 'daily') === 'pms'),
    [equipment.data],
  );
  const pmsEquipGroups = useMemo(
    () => groupBy(pmsEquipment, (r) => equipById.get(String(r['Equipment ID']))?.pmsGroup || 'Other'),
    [pmsEquipment, equipById],
  );

  async function approve(r: Row) {
    const submissionId = String(r['Submission ID']);
    const equipmentId = String(r['Equipment ID']);
    setBusyId(submissionId);
    try {
      await adminApi.approveReading(submissionId, equipmentId);
      setRowMsg((m) => ({ ...m, [submissionId]: 'Approved.' }));
      reload();
    } catch (e) {
      setRowMsg((m) => ({ ...m, [submissionId]: e instanceof Error ? e.message : 'Could not approve.' }));
    } finally {
      setBusyId('');
    }
  }

  async function downloadPmsReport(r: Row) {
    const submissionId = String(r['Submission ID']);
    const equipmentId = String(r['Equipment ID']);
    const eq = equipById.get(equipmentId);
    const tpl = templates.data?.templates.find((t) => t.templateId === eq?.templateId);
    if (!tpl) { setRowMsg((m) => ({ ...m, [submissionId]: 'Report template not found.' })); return; }
    setBusyId(submissionId);
    try {
      const effectiveReadingAt = String(r['Effective Reading At'] ?? '');
      const readingMs = new Date(effectiveReadingAt).getTime();
      const pmsDueAt = Number.isNaN(readingMs) ? undefined
        : new Date(readingMs + (eq?.targetIntervalMin ?? 21600) * 60000).toISOString();
      await sharePmsReportPdf({
        templateTitle: tpl.title,
        equipName: String(r['Equipment Name'] ?? equipmentId),
        equipLoc: String(r['Location'] ?? ''),
        equipmentId,
        submissionId,
        effectiveReadingAt,
        operatorName: String(r['Operator Name'] ?? ''),
        operatorId: String(r['Operator ID'] ?? ''),
        remarks: String(r['Observation / Remarks'] ?? ''),
        approvedByName: r['Approved By Name'] ? String(r['Approved By Name']) : undefined,
        approvedAt: r['Approved At'] ? String(r['Approved At']) : undefined,
        fields: tpl.fields.map((f) => ({ key: f.fieldKey, label: f.label, unit: f.unit, dataType: f.dataType, col: f.col })),
        row: r,
        manufacturer: eq?.manufacturer,
        pmsNumber: eq?.pmsNumber,
        uniqueId: eq?.uniqueId,
        yearOfInstallation: eq?.yearOfInstallation,
        serialNumber: eq?.serialNumber,
        capacity: eq?.capacity,
        pmsDueAt,
      });
    } catch (e) {
      setRowMsg((m) => ({ ...m, [submissionId]: e instanceof Error ? e.message : 'Could not build the report.' }));
    } finally {
      setBusyId('');
    }
  }

  function clean(f: typeof filter) {
    const o: Record<string, string> = {};
    if (f.from) o.from = f.from;
    if (f.to) o.to = f.to;
    if (f.equipmentId) o.equipmentId = f.equipmentId;
    if (f.operatorId) o.operatorId = f.operatorId;
    return o;
  }

  const pmsRows = useMemo(() => {
    const rows = (data?.rows ?? []).filter((r) => equipById.get(String(r['Equipment ID']))?.category === 'pms');
    if (applied.approval === 'approved') return rows.filter((r) => !!r['Approved By Name']);
    if (applied.approval === 'pending') return rows.filter((r) => !r['Approved By Name']);
    return rows;
  }, [data, equipById, applied.approval]);

  const groups = useMemo(
    () => groupBy(pmsRows, (r) => equipById.get(String(r['Equipment ID']))?.pmsGroup || 'Other'),
    [pmsRows, equipById],
  );

  const metricCols = useMemo(() => {
    if (!pmsRows.length) return [] as string[];
    return Object.keys(pmsRows[0]).filter((k) => !LEAD.has(k));
  }, [pmsRows]);

  const pendingCount = pmsRows.filter((r) => !r['Approved By Name']).length;

  return (
    <div>
      <h1>PMS reports</h1>
      <p className="muted" style={{ marginTop: -6 }}>
        Preventive-maintenance checklists — review, approve, and generate the per-visit report PDF.
        Daily readings live under <strong>Readings</strong>.
      </p>
      <div className="filters">
        <div><label>From</label><input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} /></div>
        <div><label>To</label><input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} /></div>
        <div><label>Equipment</label>
          <select value={filter.equipmentId} onChange={(e) => setFilter({ ...filter, equipmentId: e.target.value })}>
            <option value="">all PMS equipment</option>
            {pmsEquipGroups.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.items.map((r, i) => (
                  <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment Name'])}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div><label>Operator ID</label><input value={filter.operatorId} onChange={(e) => setFilter({ ...filter, operatorId: e.target.value.toUpperCase() })} /></div>
        <div><label>Approval</label>
          <select value={filter.approval} onChange={(e) => setFilter({ ...filter, approval: e.target.value })}>
            <option value="">any</option>
            <option value="pending">pending review</option>
            <option value="approved">approved</option>
          </select>
        </div>
        <button className="sm" onClick={() => setApplied({ ...filter })}>Apply</button>
      </div>

      <Banner error={error} />
      {loading && <div className="spinner" />}
      {!loading && pmsRows.length === 0 && <Empty icon="🛠️">No PMS readings match.</Empty>}
      {!loading && pmsRows.length > 0 && pendingCount > 0 && (
        <p className="tiny" style={{ color: 'var(--warn)', fontWeight: 600 }}>{pendingCount} awaiting review.</p>
      )}

      {groups.map((g) => (
        <div key={g.label} style={{ marginBottom: 4 }}>
          <h3 className="pmsGroup-head">{g.label} <span className="muted">({g.items.length})</span></h3>
          {g.items.map((r, i) => {
            const st = String(r['Abnormal Status'] ?? 'normal').toLowerCase();
            const tone = st === 'critical' ? 'bad' : st === 'warning' ? 'warn' : 'ok';
            const submissionId = String(r['Submission ID']);
            const approvedByName = r['Approved By Name'] ? String(r['Approved By Name']) : '';
            const busy = busyId === submissionId;
            return (
              <details className="rrow" key={i}>
                <summary>
                  <span className={`chip ${tone}`}>{st}</span>
                  <span>
                    <span className="rtime">{String(r['Date'])} {String(r['Time'])}</span>
                    <span className="rmeta"> · {String(r['Equipment Name'])} · {String(r['Operator Name'])}</span>
                  </span>
                  <span className="muted" style={{ fontSize: '.8rem' }}>{String(r['Source'] ?? '')}</span>
                </summary>
                <div className="rdetail">
                  <div className="kv">
                    {metricCols.map((c) => (
                      <div key={c}><span className="kk">{prettyCol(c)}</span><span>{String(r[c] ?? '—')}</span></div>
                    ))}
                  </div>
                  {r['Observation / Remarks'] ? <p style={{ marginTop: 10 }}><span className="muted">Remarks: </span>{String(r['Observation / Remarks'])}</p> : null}
                  {r['Abnormal Fields'] ? <p className="tiny" style={{ color: 'var(--warn)' }}>{String(r['Abnormal Fields'])}</p> : null}
                  <p className="muted tiny">ID {submissionId.slice(0, 8)} · server {String(r['Server Timestamp'])}</p>

                  <div className="spread" style={{ marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
                    <span className="tiny">
                      {approvedByName
                        ? <span style={{ color: 'var(--ok)' }}>✓ Approved by {approvedByName}</span>
                        : <span className="muted">Not yet reviewed</span>}
                    </span>
                    <span style={{ display: 'flex', gap: 8 }}>
                      {!approvedByName && (
                        <button className="secondary xs" disabled={busy} onClick={() => void approve(r)}>
                          {busy ? '…' : 'Approve'}
                        </button>
                      )}
                      <button className="secondary xs" disabled={busy} onClick={() => void downloadPmsReport(r)}>
                        {busy ? '…' : 'PMS Report PDF'}
                      </button>
                    </span>
                  </div>
                  {rowMsg[submissionId] && <p className="tiny" style={{ marginTop: 6 }}>{rowMsg[submissionId]}</p>}
                </div>
              </details>
            );
          })}
        </div>
      ))}
    </div>
  );
}

const COL_LABEL = Object.fromEntries(FIELD_META.map((f) => [f.col, f.label]));
function prettyCol(c: string) { return COL_LABEL[c] ?? c; }
